import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, LockOpen, Pencil, Bike, Loader2, FileSpreadsheet, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { formatoMonto } from '../../utils/menuComida';

// Cierre del pedido del día (Reportes de Comida, solo Procura): por fecha y turno,
// Procura indica cuántos deliveries hubo. El delivery se carga al departamento con
// más pedidos del turno (o al que elija entre los presentes) y queda en el Excel
// del detalle y en el dashboard.

const API = '/api/comidas/cierres';
const TURNOS = ['Desayuno', 'Almuerzo', 'Cena'];
const MAX_DELIVERIES = 50;

const INPUT = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue';

// '2026-10-07' -> '07/10'
const diaMes = (iso) => {
    const [, m, d] = (iso || '').split('-');
    return d && m ? `${d}/${m}` : iso;
};

// "Cena ... cerrada", "Almuerzo ... cerrado"
const cerradoDe = (turno) => (turno === 'Cena' ? 'cerrada' : 'cerrado');

const textoDeliveries = (n) => (n ? `${n} ${n === 1 ? 'delivery' : 'deliveries'}` : 'sin delivery');

const etiquetaCentroCosto = (codigo, nombre) => (codigo ? `${codigo}${nombre ? ` · ${nombre}` : ''}` : 'Sin centro de costo');

// Aviso de un turno ya cerrado, con Editar y Reabrir.
const AvisoCierre = ({ cierre, onEditar, onReabrir }) => {
    const [confirmando, setConfirmando] = useState(false);
    const [reabriendo, setReabriendo] = useState(false);
    const n = cierre.delivery_cantidad || 0;

    const reabrir = async () => {
        setReabriendo(true);
        await onReabrir(cierre);
        setReabriendo(false);
        setConfirmando(false);
    };

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <Lock size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">{cierre.turno} del {diaMes(cierre.fecha)} {cerradoDe(cierre.turno)}</strong>
                    {cierre.cerrado_por && <> por {cierre.cerrado_por}</>}
                    {' · '}{textoDeliveries(n)}
                    {n > 0 && (
                        <> · <span className="font-semibold tabular-nums">{formatoMonto(cierre.delivery_monto)}</span>
                            {cierre.departamento_gasto && <span className="text-gray-500"> ({cierre.departamento_gasto})</span>}</>
                    )}
                    {cierre.pedidos !== null && cierre.pedidos !== undefined && (
                        <span className="text-gray-500"> · {cierre.pedidos} pedido{cierre.pedidos === 1 ? '' : 's'}</span>
                    )}
                </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {confirmando ? (
                    <>
                        <span className="text-xs text-gray-600">¿Reabrir? Se borra su delivery.</span>
                        <button onClick={() => setConfirmando(false)} disabled={reabriendo}
                            className="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                            No
                        </button>
                        <button onClick={reabrir} disabled={reabriendo}
                            className="px-3 py-1.5 text-xs text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                            {reabriendo ? <Loader2 size={12} className="animate-spin" /> : <LockOpen size={12} />} Sí, reabrir
                        </button>
                    </>
                ) : (
                    <>
                        <button onClick={() => onEditar(cierre.turno)}
                            className="px-3 py-1.5 text-xs font-medium text-sura-blue bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                            <Pencil size={12} /> Editar
                        </button>
                        <button onClick={() => setConfirmando(true)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                            <LockOpen size={12} /> Reabrir
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

// Modal para cerrar (o corregir) el pedido de un turno.
const ModalCierre = ({ fecha, turnoInicial, cierres, onCerrar, onGuardado, onExportar }) => {
    const [turno, setTurno] = useState(turnoInicial || '');
    const [resumen, setResumen] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [cantidad, setCantidad] = useState('0');
    const [departamento, setDepartamento] = useState('');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!turno) return undefined;
        let vigente = true;
        setCargando(true);
        setResumen(null);
        axiosInstance.get(`${API}/resumen`, { params: { fecha, turno } })
            .then(res => {
                if (!vigente) return;
                const r = res.data;
                setResumen(r);
                setCantidad(String(r.cierre ? r.cierre.delivery_cantidad || 0 : 0));
                setDepartamento((r.cierre && r.cierre.departamento_gasto) || r.departamento_defecto || '');
            })
            .catch(err => {
                if (vigente) toast.error(err.response?.data?.error || 'No se pudo cargar el resumen del turno');
            })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [fecha, turno]);

    const cierre = resumen?.cierre || null;
    const precio = resumen ? Number(resumen.precio) : null;
    const n = cantidad === '' ? NaN : Number(cantidad);
    const valido = Number.isInteger(n) && n >= 0 && n <= MAX_DELIVERIES;

    // Opciones de departamento: los presentes en el turno. Al corregir se mantiene
    // el que ya tenía el cierre aunque hoy no tenga pedidos.
    const opciones = resumen ? [...resumen.departamentos] : [];
    if (cierre && cierre.departamento_gasto && !opciones.some(d => d.departamento === cierre.departamento_gasto)) {
        opciones.push({
            departamento: cierre.departamento_gasto,
            centro_costo: cierre.centro_costo,
            centro_costo_nombre: cierre.centro_costo_nombre,
            pedidos: 0,
        });
    }
    const elegido = opciones.find(d => d.departamento === departamento) || null;
    const sinDepartamento = valido && n > 0 && !elegido;
    const precioCambio = cierre && n > 0 && cierre.delivery_precio !== null && precio !== null
        && Number(cierre.delivery_precio) !== precio;

    const guardar = async (e) => {
        e.preventDefault();
        if (!valido) {
            toast.error(`La cantidad de deliveries debe ser un número entero entre 0 y ${MAX_DELIVERIES}`);
            return;
        }
        if (sinDepartamento) {
            toast.error('No hay pedidos en este turno a los que cargar el delivery');
            return;
        }
        setGuardando(true);
        try {
            const res = await axiosInstance.post(API, {
                fecha,
                turno,
                delivery_cantidad: n,
                departamento_gasto: elegido ? elegido.departamento : null,
            });
            toast.success(`${turno} del ${diaMes(fecha)} ${cerradoDe(turno)} · ${textoDeliveries(n)}`);
            onGuardado(res.data);
            onCerrar();
            await onExportar(fecha, turno);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo cerrar el pedido del día');
        } finally {
            setGuardando(false);
        }
    };

    const turnosCerrados = new Set(cierres.map(c => c.turno));

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
            <motion.form onSubmit={guardar}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Lock size={18} className="text-sura-blue" /> {cierre ? 'Editar cierre del pedido' : 'Cerrar pedido del día'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">{diaMes(fecha)}{turno ? ` · ${turno}` : ''}</p>
                    </div>
                    <button type="button" onClick={onCerrar} className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-5 overflow-y-auto">
                    {/* Turno: si el filtro ya tenía uno no se pregunta; solo se elige con "Todos los turnos" */}
                    {!turnoInicial && (
                    <div>
                        <p className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-2">Turno</p>
                        <div className="grid grid-cols-3 gap-2">
                            {TURNOS.map(t => (
                                <button type="button" key={t} onClick={() => setTurno(t)} disabled={guardando}
                                    className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${turno === t
                                        ? 'bg-sura-blue text-white border-sura-blue'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
                                    {t}
                                    {turnosCerrados.has(t) && (
                                        <span className={`block text-[11px] font-normal ${turno === t ? 'text-white/80' : 'text-emerald-600'}`}>
                                            {cerradoDe(t)}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    )}

                    {!turno ? (
                        <p className="text-sm text-gray-500">Elige el turno que vas a cerrar.</p>
                    ) : cargando || !resumen ? (
                        <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between bg-gray-50 rounded-2xl px-4 py-3 text-sm">
                                <span className="text-gray-600">Pedidos del turno</span>
                                <span className="font-bold text-gray-900 tabular-nums">{resumen.pedidos}</span>
                            </div>

                            <div>
                                <label className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                                    <Bike size={14} /> ¿Cuántos deliveries hubo?
                                </label>
                                <div className="flex flex-wrap items-center gap-3">
                                    <input type="number" inputMode="numeric" min="0" max={MAX_DELIVERIES} step="1" autoFocus
                                        value={cantidad} onChange={e => setCantidad(e.target.value)}
                                        className={`${INPUT} w-24`} />
                                    <span className="text-sm text-gray-600 tabular-nums">
                                        {valido
                                            ? <>{n} × {formatoMonto(precio)} = <strong className="text-gray-900">{formatoMonto(n * precio)}</strong></>
                                            : '—'}
                                    </span>
                                </div>
                                {precioCambio && (
                                    <p className="text-xs text-amber-600 mt-1.5">
                                        Se recalcula con el precio vigente ({formatoMonto(precio)}); al cerrar era {formatoMonto(cierre.delivery_precio)}.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-2 block">El delivery se carga a</label>
                                {opciones.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">No hay pedidos en este turno: solo se puede cerrar sin delivery.</p>
                                ) : (
                                    <>
                                        <select value={departamento} onChange={e => setDepartamento(e.target.value)} className={INPUT}>
                                            {opciones.map((d, i) => (
                                                <option key={d.departamento} value={d.departamento}>
                                                    {d.departamento} ({d.pedidos} pedido{d.pedidos === 1 ? '' : 's'}){i === 0 && d.pedidos > 0 ? ' · mayoría' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {elegido && (
                                            <p className="text-xs text-gray-500 mt-1.5">
                                                Centro de costo: <span className="font-medium text-gray-700">{etiquetaCentroCosto(elegido.centro_costo, elegido.centro_costo_nombre)}</span>
                                            </p>
                                        )}
                                    </>
                                )}
                                {sinDepartamento && (
                                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Elige a qué departamento se carga el delivery.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-3">
                    <button type="button" onClick={onCerrar} disabled={guardando}
                        className="flex-1 px-5 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors">
                        Cancelar
                    </button>
                    <button type="submit" disabled={!turno || !resumen || cargando || guardando || !valido || sinDepartamento}
                        className="flex-1 px-5 py-3 text-white bg-sura-blue hover:bg-sura-blue-dark rounded-xl font-medium transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-50">
                        {guardando ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
                        {cierre ? 'Guardar y exportar' : 'Cerrar y exportar'}
                    </button>
                </div>
            </motion.form>
        </div>
    );
};

const CierrePedidoDia = ({ fecha, turno, onExportar }) => {
    const [cierres, setCierres] = useState([]);
    const [pedidosPorTurno, setPedidosPorTurno] = useState({});
    const [cargando, setCargando] = useState(false);
    const [modal, setModal] = useState(null); // { turno } con el turno inicial ('' = elegir)

    const cargar = useCallback(async () => {
        if (!fecha) {
            setCierres([]);
            setPedidosPorTurno({});
            return;
        }
        setCargando(true);
        try {
            const res = await axiosInstance.get(API, { params: { fecha } });
            setCierres(res.data?.cierres || []);
            setPedidosPorTurno(res.data?.pedidos_por_turno || {});
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudieron cargar los cierres del día');
        } finally {
            setCargando(false);
        }
    }, [fecha]);

    useEffect(() => { cargar(); }, [cargar]);

    const reabrir = async (cierre) => {
        try {
            await axiosInstance.delete(`${API}/${cierre.id}`);
            toast.success(`${cierre.turno} del ${diaMes(cierre.fecha)} reabiert${cierre.turno === 'Cena' ? 'a' : 'o'}`);
            cargar();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo reabrir el turno');
        }
    };

    if (!fecha) return null;

    const todos = !turno || turno === 'Todos';
    const visibles = todos ? cierres : cierres.filter(c => c.turno === turno);
    const cerrados = new Set(cierres.map(c => c.turno));
    // Solo cuenta como "sin cerrar" un turno que tiene pedidos ese día
    const pendientes = (todos ? TURNOS : [turno]).filter(t => !cerrados.has(t) && (pedidosPorTurno[t] || 0) > 0);

    return (
        <div className="space-y-3 mb-8">
            {visibles.map(c => (
                <AvisoCierre key={c.id} cierre={c} onEditar={t => setModal({ turno: t })} onReabrir={reabrir} />
            ))}

            {pendientes.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-dashed border-gray-200 rounded-2xl px-4 py-3">
                    <p className="text-sm text-gray-600 flex-1">
                        {todos
                            ? <>Sin cerrar el {diaMes(fecha)}: <strong className="text-gray-900">{pendientes.join(', ')}</strong>. Al cerrar indicas cuántos deliveries hubo.</>
                            : <><strong className="text-gray-900">{turno} del {diaMes(fecha)}</strong> sin cerrar. Al cerrar indicas cuántos deliveries hubo.</>}
                    </p>
                    <button onClick={() => setModal({ turno: pendientes.length === 1 ? pendientes[0] : '' })} disabled={cargando}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-sura-blue hover:bg-sura-blue-dark text-white text-sm font-medium rounded-xl transition-colors shadow-sm disabled:opacity-50 shrink-0">
                        {cargando ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Cerrar pedido del día
                    </button>
                </div>
            )}

            <AnimatePresence>
                {modal && (
                    <ModalCierre
                        key="modal-cierre"
                        fecha={fecha}
                        turnoInicial={modal.turno}
                        cierres={cierres}
                        onCerrar={() => setModal(null)}
                        onGuardado={() => cargar()}
                        onExportar={onExportar}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default CierrePedidoDia;
